import { navigate } from 'gatsby'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { usePageContext } from '../../../hooks/usePageContext'
import { sanityTwoStepDisclaimerToTwoStepDisclaimerProps } from '../../../mapping/DisclaimerMapper'
import { getCookieValue } from '../../../utilities/cookie'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { Grid } from '../../Grid'
import { TwoStepDisclaimer } from './TwoStepDisclaimer'
import { useSiteWideDisclaimer } from './useSiteWideDisclaimer'

export const SiteWideDisclaimer = ({ children }: PropsWithChildren) => {
  const [hydrated, setHydrated] = useState(false)
  const pageContext = usePageContext()

  const {
    accessGranted,
    disclaimerData,
    hasAccessToPath,
    onGrantAccess,
    setAccessGranted,
  } = useSiteWideDisclaimer()

  const disclaimerProps = useMemo(
    () =>
      disclaimerData
        ? sanityTwoStepDisclaimerToTwoStepDisclaimerProps(disclaimerData)
        : undefined,
    [disclaimerData]
  )

  useEffect(() => {
    setHydrated(true)
  }, [])

  // Note: This useEffect is put here instead of inside the useSiteWideDisclaimer hook, to make sure it does not run
  // every time a component uses the hook.
  useEffect(() => {
    if (pageContext.path) {
      if (hasAccessToPath(pageContext.path)) {
        setAccessGranted(true)
      } else {
        setAccessGranted(false)

        const cookieValue = getCookieValue('eqt_tsd')

        if (!isNullOrWhiteSpace(cookieValue)) {
          // The cookie must contain another page's path - redirect to it.
          navigate(cookieValue)
        }
      }
    }
  }, [hasAccessToPath, pageContext.path, setAccessGranted])

  if (accessGranted || !disclaimerProps) {
    return <>{children}</>
  } else {
    if (hydrated) {
      return (
        <Grid>
          <div className="col-span-full w-full max-w-[690px] justify-self-center">
            <TwoStepDisclaimer {...disclaimerProps} onAccept={onGrantAccess} />
          </div>
        </Grid>
      )
    } else {
      // Show a blank page until hydration, to not flash the disclaimer page briefly even if the user
      // has previously been granted access.
      return null
    }
  }
}
